import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { ToolOutput } from 'src/services/mockDataService';
import { Card, Text } from 'src/shared';
import { Colors } from 'src/utils/color';
import { Size } from 'src/shared/text';

interface ResponseCardProps {
  toolOutput: ToolOutput;
}

const CardContainer = styled(Card)<{ status: string }>`
  margin-bottom: 16px;
  border-left: 4px solid ${props => {
    switch (props.status) {
      case 'success': return Colors.theme.primary;
      case 'error': return Colors.extra.red;
      case 'pending': return Colors.extra.dark_gray;
      default: return Colors.extra.light_gray;
    }
  }};
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const ToolName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusIcon = styled.div<{ status: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => {
    switch (props.status) {
      case 'success': return '#10b981';
      case 'error': return '#ef4444';
      case 'pending': return '#f59e0b';
      default: return Colors.extra.light_gray;
    }
  }};
`;

const Timestamp = styled(Text)`
  color: ${Colors.extra.dark_gray};
  font-size: 12px;
`;

const ResultContent = styled.div`
  margin-bottom: 12px;
`;

const SummaryText = styled(Text)`
  line-height: 1.5;
  margin-bottom: 8px;
`;

const DataSection = styled.div`
  background-color: ${Colors.extra.light_gray}20;
  padding: 12px;
  border-radius: 6px;
  margin-top: 8px;
`;

const DataItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ChartContainer = styled.div`
  margin-top: 12px;
  padding: 12px;
  background-color: ${Colors.extra.light_gray}10;
  border-radius: 6px;
  text-align: center;
`;

const Duration = styled(Text)`
  color: ${Colors.extra.dark_gray};
  font-size: 11px;
  font-style: italic;
`;

const ResponseCard: FunctionComponent<ResponseCardProps> = ({ toolOutput }) => {
  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (duration?: number) => {
    if (!duration) return '';
    return `${duration}ms`;
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'success': return 'Success';
      case 'error': return 'Error';
      case 'pending': return 'Pending';
      default: return 'Unknown';
    }
  };

  return (
    <CardContainer status={toolOutput.status}>
      <CardHeader>
        <ToolName>
          <StatusIcon status={toolOutput.status} />
          <Text fontSize={Size.Medium} fontWeight="600">
            {toolOutput.tool.replace(/_/g, ' ').toUpperCase()}
          </Text>
        </ToolName>
        <div>
          <Timestamp>{formatTimestamp(toolOutput.timestamp)}</Timestamp>
          {toolOutput.duration && (
            <Duration>Duration: {formatDuration(toolOutput.duration)}</Duration>
          )}
        </div>
      </CardHeader>

      <ResultContent>
        {toolOutput.result.summary && (
          <SummaryText fontSize={Size.Small}>
            {toolOutput.result.summary}
          </SummaryText>
        )}

        {toolOutput.result.data && (
          <DataSection>
            <Text fontSize={Size.XSmall} fontWeight="600" marginBottom={8}>
              Data:
            </Text>
            {Object.entries(toolOutput.result.data).map(([key, value]) => (
              <DataItem key={key}>
                <Text fontSize={Size.XSmall} color={Colors.extra.dark_gray}>
                  {key}:
                </Text>
                <Text fontSize={Size.XSmall} fontWeight="500">
                  {typeof value === 'number' ? value.toFixed(2) : String(value)}
                </Text>
              </DataItem>
            ))}
          </DataSection>
        )}

        {toolOutput.result.chart && (
          <ChartContainer>
            <Text fontSize={Size.XSmall} color={Colors.extra.dark_gray} marginBottom={8}>
              Chart: {toolOutput.result.chart.type}
            </Text>
            <div style={{ 
              height: '60px', 
              background: 'linear-gradient(90deg, #3b82f6, #1d4ed8)',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'end',
              padding: '8px',
              gap: '4px'
            }}>
              {toolOutput.result.chart.data?.map((value: number, index: number) => (
                <div
                  key={index}
                  style={{
                    height: `${(value / Math.max(...toolOutput.result.chart.data)) * 100}%`,
                    background: '#ffffff',
                    flex: 1,
                    borderRadius: '2px 2px 0 0',
                    minHeight: '4px'
                  }}
                />
              ))}
            </div>
          </ChartContainer>
        )}

        {toolOutput.result.image && (
          <div style={{ marginTop: '12px' }}>
            <Text fontSize={Size.XSmall} color={Colors.extra.dark_gray} marginBottom={8}>
              Image:
            </Text>
            <div style={{
              width: '100%',
              height: '120px',
              background: `url(${toolOutput.result.image}) center/cover`,
              borderRadius: '6px',
              border: `1px solid ${Colors.extra.light_gray}`
            }} />
          </div>
        )}
      </ResultContent>

      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        paddingTop: '8px',
        borderTop: `1px solid ${Colors.extra.light_gray}20`
      }}>
        <Text fontSize={Size.XSmall} color={Colors.extra.dark_gray}>
          Status: {getStatusText(toolOutput.status)}
        </Text>
        <Text fontSize={Size.XSmall} color={Colors.extra.dark_gray}>
          ID: {toolOutput.id}
        </Text>
      </div>
    </CardContainer>
  );
};

export default ResponseCard;

